import React, { useCallback } from 'react';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import SectionMessage from '@atlaskit/section-message';
import {
	ROUTE_NAMES_DASHBOARD_VIEW,
	ROUTE_NAMES_SYSTEM_DASHBOARD_EDIT,
} from '@atlassian/jira-common-constants/src/spa-routes';
import LoginRedirect from '@atlassian/jira-common-navigation/src/login-redirect';
import {
	SYSTEM_DASHBOARD_ID,
	DASHBOARD_EDIT_SOURCENAME,
	VIEW_DASHBOARD_EDITABLE_EXPERIENCE,
} from '@atlassian/jira-dashboard-common/src/constants.tsx';
import { DeprecationBanner } from '@atlassian/jira-dashboard-common/src/ui/deprecation-banner';
import { DashboardControllerContextConsumer } from '@atlassian/jira-dashboard-internal-common/src/controllers/dashboard/context.tsx';
import { DashboardController } from '@atlassian/jira-dashboard-internal-common/src/controllers/dashboard/main.tsx';
import { DashboardPageContainer } from '@atlassian/jira-dashboard-internal-common/src/ui/container/main.tsx';
import { EngagementBanners } from '@atlassian/jira-dashboard-internal-common/src/ui/dashboard-content/engagement-banners/index.tsx';
import { ColumnLayout } from '@atlassian/jira-dashboard-internal-common/src/ui/dashboard-content/layout/main.tsx';
import { DashboardContentContainer } from '@atlassian/jira-dashboard-internal-common/src/ui/dashboard-content/main.tsx';
import { useConnectGadgetTitles } from '@atlassian/jira-dashboard-internal-common/src/utils/use-gadget-title/index.tsx';
import { DashboardPermissionErrorPageAsync } from '@atlassian/jira-error-pages/src/async.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useRouteByName } from '@atlassian/jira-platform-router-utils';
import { useDashboardResource } from '@atlassian/jira-router-resources-dashboard/src/index.tsx';
import { Redirect, generatePath } from '@atlassian/react-resource-router';
import { RightPanelLayoutContainer } from './common/right-panel-layout';
import { useLayoutChange } from './controllers/layout-change';
import messages from './messages';
import { GadgetEditable } from './ui/gadget-editable/index.tsx';
import { AsyncAddGadgetPanel } from './ui/header-editable/add-gadget-button/add-gadget-panel/async.tsx';
import { addGadgetPanelId } from './ui/header-editable/add-gadget-button/add-gadget-panel/constants.tsx';
import { HeaderEditable } from './ui/header-editable/main.tsx';
import { ColumnPlaceholder } from './ui/placeholder/index.tsx';
import { useLayoutChangeErrorFlag } from './utils/change-layout-error';

export const DashboardEditable = () => {
	const { data, error, loading } = useDashboardResource();
	const { registerConnectGadget, unregisterConnectGadget, setCustomTitle } =
		useConnectGadgetTitles();
	const setLayout = useLayoutChange();
	const showLayoutFlag = useLayoutChangeErrorFlag();
	const { formatMessage } = useIntl();

	const onLayoutHandler = useCallback<(...rest: Parameters<typeof setLayout>) => void>(
		(...args) => {
			setLayout(...args).catch(showLayoutFlag);
		},
		[setLayout, showLayoutFlag],
	);

	return (
		<DashboardContentContainer
			analyticsExperience={VIEW_DASHBOARD_EDITABLE_EXPERIENCE}
			saveCustomGadgetTitle={setCustomTitle}
			resourceData={{ data, error, loading }}
			permissionErrorFallback={<DashboardPermissionErrorPageAsync />}
		>
			{data ? (
				<DashboardController id={data.id} automaticRefreshMs={data.automaticRefreshMs}>
					<RightPanelLayoutContainer
						isOpenByDefault
						defaultPanelId={`${addGadgetPanelId}-0`}
						rightContent={<AsyncAddGadgetPanel />}
					>
						<HeaderEditable
							data={data}
							banners={
								<>
									<SectionMessage appearance="discovery">
										{formatMessage(messages.editingInfo)}
									</SectionMessage>
									<DeprecationBanner gadgets={data.gadgets} gadgetId={data.id} />
									<EngagementBanners />
								</>
							}
							onLayoutSelected={onLayoutHandler}
						/>
						<ColumnLayout
							data={data}
							dnd={{
								DragDropContext,
								Draggable,
								Droppable,
							}}
							columnPlaceholder={<ColumnPlaceholder />}
							layoutPlaceholder={null}
							onLayoutReposition={onLayoutHandler}
						>
							{({ isAnyDragging, ...gadgetProps }) => (
								<DashboardControllerContextConsumer>
									{({ refreshId, onMount }) => (
										<GadgetEditable
											{...gadgetProps}
											isConnectDisabled={isAnyDragging}
											dashboardId={data.id}
											refreshId={refreshId}
											onMount={onMount}
											onRegisterConnectGadget={registerConnectGadget}
											onUnregisterConnectGadget={unregisterConnectGadget}
										/>
									)}
								</DashboardControllerContextConsumer>
							)}
						</ColumnLayout>
					</RightPanelLayoutContainer>
				</DashboardController>
			) : null}
		</DashboardContentContainer>
	);
};

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default () => {
	const { data, error, loading } = useDashboardResource();
	const { formatMessage } = useIntl();
	const { path: viewPath } = useRouteByName(ROUTE_NAMES_DASHBOARD_VIEW) ?? {};
	const { path: systemDashboardPath } = useRouteByName(ROUTE_NAMES_SYSTEM_DASHBOARD_EDIT) ?? {};
	const { id, title } = data ?? {};

	if (id === SYSTEM_DASHBOARD_ID) {
		return systemDashboardPath != null ? (
			<Redirect to={generatePath(systemDashboardPath)} />
		) : (
			<DashboardPermissionErrorPageAsync />
		);
	}

	return (
		<DashboardPageContainer
			analyticsSourceName={DASHBOARD_EDIT_SOURCENAME}
			analyticsExperience={VIEW_DASHBOARD_EDITABLE_EXPERIENCE}
			documentTitle={
				title
					? formatMessage(messages.pageTitle, { dashboardName: title })
					: formatMessage(messages.pageTitleFallback)
			}
			unauthorisedFallback={
				viewPath && id ? (
					<Redirect to={generatePath(viewPath, { dashboardId: id })} />
				) : (
					<DashboardPermissionErrorPageAsync />
				)
			}
			unauthenticatedFallback={<LoginRedirect />}
			resourceData={{ data, error, loading }}
		>
			<DashboardEditable />
		</DashboardPageContainer>
	);
};
